/**
 * CompanionShell — mobile/phone responsive shell with bottom-nav.
 *
 * Standard mobile chrome, smaller tap targets, longer session. No
 * idle-logout watchdog (companion is the patient's own device).
 *
 * Role-aware:
 *   · PATIENT — wellness bottom nav + an Insurance tab. Inside the
 *     insurance section the dashboards' own <SectionTabs> pills switch
 *     sections (a bottom bar can't fit up to 8 of them).
 *   · insurance pros — no wellness tabs (RBAC'd off anyway); just the
 *     brand bar with sign out, and <SectionTabs> in the page.
 */

import { Suspense, lazy } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  FileText,
  FlaskConical,
  Home as HomeIcon,
  LineChart,
  LogOut,
  ShieldCheck,
  User,
} from 'lucide-react';
import { logout } from '@cardioq/api-client';
import { cn } from '@/lib/cn';
import { useAuth } from '@/hooks/useAuth';
import { signInPathFor } from '@/lib/signInPath';
import { useInsuranceNavGroup } from '@/lib/insuranceNav';
import { Logo } from './Logo';

// Lazy: insurance/lib/api.ts calls getApiClient() at module scope, which
// throws unless configureApiClient() (main.tsx) has already run.
// CompanionShell is part of the app's eager import chain, so this must
// stay a dynamic import to not pull that module in early — same reasoning
// as DesktopShell's identical import.
const NotificationBell = lazy(() =>
  import('@/insurance/components/NotificationBell').then((m) => ({ default: m.NotificationBell })),
);

const NAV = [
  { to: '/home', label: 'Home', icon: HomeIcon },
  { to: '/history', label: 'History', icon: LineChart },
  { to: '/compute', label: 'Test', icon: FlaskConical }, // DEV — remove before prod
  { to: '/documents', label: 'Docs', icon: FileText },
  { to: '/profile', label: 'Profile', icon: User },
];

export function CompanionShell({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation();
  const { role, logout: clearLocalAuth } = useAuth();
  const navigate = useNavigate();
  const insurance = useInsuranceNavGroup();
  const isPatient = role === 'PATIENT';

  const nav = isPatient
    ? [
        ...NAV,
        // Same entry point as the desktop sidebar's Insurance group.
        ...(insurance ? [{ to: insurance.base, label: 'Insurance', icon: ShieldCheck }] : []),
      ]
    : [];

  async function handleSignOut() {
    const to = signInPathFor(role);
    await logout();
    clearLocalAuth();
    navigate(to, { replace: true });
  }

  return (
    <div className="flex min-h-screen flex-col bg-brand-bg text-brand-slate-100">
      {/* Top brand bar */}
      <header className="border-b border-white/[0.06] bg-brand-surface/30 backdrop-blur-md">
        <div className="mx-auto flex max-w-md items-center justify-between px-5 py-3">
          <Logo className="h-9 w-9" />
          <div className="flex items-center gap-2">
            {/* The patient's primary device — DesktopShell mounts the same
                bell for desktop sessions, but a mobile-only patient must
                not be left with no notifications at all (D-10b). */}
            {isPatient ? (
              <Suspense fallback={null}>
                <NotificationBell persona="PATIENT" />
              </Suspense>
            ) : null}
            {/* Pros have no Profile page to sign out from — same button,
                same flow as the desktop top bar. */}
            {!isPatient ? (
              <button
                type="button"
                onClick={handleSignOut}
                className="inline-flex items-center gap-1.5 rounded-full border border-brand-slate-700/60 bg-brand-slate-900/40 px-3 py-1.5 text-xs text-brand-slate-300 transition-colors hover:text-brand-slate-100 focus-ring"
              >
                <LogOut className="h-3.5 w-3.5" />
                Sign out
              </button>
            ) : null}
          </div>
        </div>
      </header>

      {/* Main scrollable area — leaves room for bottom nav */}
      <main className={cn('flex-1 overflow-y-auto', nav.length > 0 && 'pb-20')}>
        <div className="mx-auto max-w-md px-5 py-6">{children}</div>
      </main>

      {/* Fixed bottom nav */}
      {nav.length > 0 ? (
        <nav className="fixed inset-x-0 bottom-0 z-30 border-t border-white/[0.08] bg-brand-surface/80 backdrop-blur-lg">
          <div className="mx-auto flex max-w-md items-stretch justify-around">
            {nav.map(({ to, label, icon: Icon }) => {
              const active =
                to === insurance?.base ? pathname.startsWith('/insurance') : pathname.startsWith(to);
              return (
                <Link
                  key={to}
                  to={to}
                  className={cn(
                    'flex flex-1 flex-col items-center gap-1 px-2 py-3 text-[11px] font-medium transition-colors focus-ring',
                    'min-h-[64px]', // comfortable mobile tap target
                    active ? 'text-brand-cyan-400' : 'text-brand-slate-400',
                  )}
                >
                  <Icon className="h-5 w-5" />
                  {label}
                </Link>
              );
            })}
          </div>
        </nav>
      ) : null}
    </div>
  );
}
